import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-duplicates
import { format, parseISO } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { ptBR } from 'date-fns/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  FiActivity,
  FiAlertCircle,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiHome,
  FiMapPin,
  FiTarget,
  FiTrendingUp,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';

import BasePage from '../../components/BasePage';
import ChartTooltip from './ChartTooltip';
import BrazilMap, { MapCityEntry, MapMode } from './BrazilMap';
import {
  PALETTE,
  STATUS_COLORS,
  PRIORITY_COLORS,
  ACTIVITY_COLORS,
} from './chartTheme';
import {
  Card,
  CardHeader,
  ChartBody,
  CompositionSection,
  DashboardContainer,
  DonutCenter,
  DonutLayout,
  EmptyState,
  FadeIn,
  GaugeCenter,
  InsightPill,
  KpiCell,
  KpiRow,
  LegendItem,
  LegendList,
  MapLegend,
  MiddleRow,
  RankedBarList,
  RankedBarRow,
  Skeleton,
  SkeletonCircle,
  SkeletonRowSpread,
  SkeletonStack,
  StackedBar,
  StatTile,
  StatTiles,
  ToggleButton,
  ToggleGroup,
  TopBar,
  TripleRow,
} from './styles';

/* ─── Types ─── */

interface IOverview {
  totalComplaints: number;
  openComplaints: number;
  totalWorkers: number;
  totalCompanies: number;
  totalPosts: number;
  totalSyndicates?: number;
}

interface INameValue {
  name: string;
  value: number;
}

interface IComplaintsData {
  complaints: number;
  byStatus: INameValue[];
  byCompanies: { name: string; complaints: number }[];
}

interface IComplaintsAnalytics {
  byCity: MapCityEntry[];
  byMonth: { month: string; total: number }[];
  byPriority: { name: string; priority: number; total: number }[];
  avgResolutionDays: number | null;
}

interface IWorkersData {
  workers: number;
  gender: INameValue[];
  activity_profile: INameValue[];
  category: INameValue[];
}

interface IWorkersAnalytics {
  byMonth: { month: string; total: number }[];
  byCity: MapCityEntry[];
  unionization: {
    total: number;
    unionized: number;
    notUnionized: number;
    rate: number;
  };
  preRegistrations: number;
}

interface IResearchAnalytics {
  active: number;
  total: number;
  finished: number;
  participation: {
    participants: number;
    totalWorkers: number;
    rate: number;
  };
}

interface IUser {
  id: string;
  first_name: string;
  role: string;
  syndicate?: { nome_fantasia: string };
}

/* ─── Helpers ─── */

const formatNumber = (n: number) =>
  new Intl.NumberFormat('pt-BR').format(n || 0);

const percent = (part: number, total: number) => {
  if (!total) return 0;
  return Math.round((part / total) * 100);
};

const formatMonthLabel = (month: string) => {
  // "2025-06" → "jun/25"
  try {
    const date = parseISO(`${month}-01`);
    return format(date, 'MMM/yy', { locale: ptBR });
  } catch {
    return month;
  }
};

const GENDER_PALETTE = [PALETTE.green, PALETTE.orange, PALETTE.blue];
const getGenderColor = (idx: number) => GENDER_PALETTE[idx] || PALETTE.subtle;

/* ─── Skeletons ─── */

const KpiSkeleton: React.FC = () => (
  <>
    <Skeleton $w="60%" $h={12} $mb={10} />
    <Skeleton $w="50%" $h={28} $mb={10} />
    <Skeleton $w="70%" $h={14} />
  </>
);

const DonutSkeleton: React.FC = () => (
  <DonutLayout>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
      }}
    >
      <SkeletonCircle $size={150} />
    </div>
    <SkeletonStack>
      {[70, 55, 60, 45].map(w => (
        <SkeletonRowSpread key={w}>
          <Skeleton $w={`${w}%`} $h={12} />
          <Skeleton $w="18%" $h={12} />
        </SkeletonRowSpread>
      ))}
    </SkeletonStack>
  </DonutLayout>
);

const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 240 }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-end',
      gap: 10,
      height,
      padding: '16px 4px 4px',
    }}
  >
    {[
      { id: 'a', h: 0.45 },
      { id: 'b', h: 0.72 },
      { id: 'c', h: 0.55 },
      { id: 'd', h: 0.88 },
      { id: 'e', h: 0.62 },
      { id: 'f', h: 0.95 },
      { id: 'g', h: 0.7 },
      { id: 'h', h: 0.8 },
    ].map(({ id, h }) => (
      <div key={id} style={{ flex: 1, height: `${h * 100}%` }}>
        <Skeleton $w="100%" $h="100%" $radius={6} />
      </div>
    ))}
  </div>
);

ChartSkeleton.defaultProps = { height: 240 };

const RankedBarsSkeleton: React.FC = () => (
  <SkeletonStack $gap={14}>
    {[80, 65, 50, 40, 35, 30].map(w => (
      <SkeletonRowSpread key={w}>
        <Skeleton $w={`${w}%`} $h={10} $radius={999} />
        <Skeleton $w={40} $h={12} />
      </SkeletonRowSpread>
    ))}
  </SkeletonStack>
);

const GaugeSkeleton: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      padding: '20px 0 10px',
    }}
  >
    <SkeletonCircle $size={160} />
    <Skeleton $w={120} $h={18} />
    <SkeletonRowSpread style={{ width: '70%', marginTop: 8 }}>
      <Skeleton $w="40%" $h={14} />
      <Skeleton $w="40%" $h={14} />
    </SkeletonRowSpread>
  </div>
);

const ResearchSkeleton: React.FC = () => (
  <>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
        marginBottom: 16,
      }}
    >
      {[0, 1, 2].map(i => (
        <Skeleton key={i} $w="100%" $h={62} $radius={10} />
      ))}
    </div>
    <Skeleton $w="100%" $h={8} $radius={999} $mb={10} />
    <Skeleton $w="60%" $h={12} />
  </>
);

const CompositionSkeleton: React.FC = () => (
  <>
    <Skeleton $w="18%" $h={11} $mb={12} />
    <Skeleton $w="100%" $h={14} $radius={999} $mb={14} />
    <SkeletonRowSpread>
      {[0, 1, 2].map(i => (
        <Skeleton key={i} $w="28%" $h={12} />
      ))}
    </SkeletonRowSpread>
    <div style={{ height: 18 }} />
    <Skeleton $w="22%" $h={11} $mb={12} />
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 10,
      }}
    >
      {[0, 1, 2, 3].map(i => (
        <Skeleton key={i} $w="100%" $h={62} $radius={10} />
      ))}
    </div>
  </>
);

/* ─── Component ─── */

const Dashboard: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth() as { user: IUser };
  const isAdmin = user.role === 'admin';

  const [overview, setOverview] = useState<IOverview>();
  const [complaints, setComplaints] = useState<IComplaintsData>();
  const [complaintsAnalytics, setComplaintsAnalytics] =
    useState<IComplaintsAnalytics>();
  const [workers, setWorkers] = useState<IWorkersData>();
  const [workersAnalytics, setWorkersAnalytics] = useState<IWorkersAnalytics>();
  const [researchAnalytics, setResearchAnalytics] =
    useState<IResearchAnalytics>();

  const [mapMode, setMapMode] = useState<MapMode>('complaints');
  const [trendMode, setTrendMode] = useState<MapMode>('complaints');
  const [updatedAt, setUpdatedAt] = useState<Date>(new Date());

  useEffect(() => {
    document.title = 'Dashboard - Viva Voz - Rede Suco de Laranja';

    // Cada request é disparado independentemente — cada card usa seu próprio
    // estado como sinal de loading. A ordem de resolução não importa.
    api
      .get<IOverview>('/dashboard/overview')
      .then(r => setOverview(r.data))
      .catch(() => undefined);
    api
      .get<IComplaintsData>('/dashboard/complaints')
      .then(r => setComplaints(r.data))
      .catch(() => undefined);
    api
      .get<IComplaintsAnalytics>('/dashboard/complaints-analytics')
      .then(r => setComplaintsAnalytics(r.data))
      .catch(() => undefined);
    api
      .get<IWorkersData>('/dashboard/workers')
      .then(r => setWorkers(r.data))
      .catch(() => undefined);
    api
      .get<IWorkersAnalytics>('/dashboard/workers-analytics')
      .then(r => setWorkersAnalytics(r.data))
      .catch(() => undefined);
    api
      .get<IResearchAnalytics>('/dashboard/research-analytics')
      .then(r => setResearchAnalytics(r.data))
      .catch(() => undefined);

    setUpdatedAt(new Date());
  }, []);

  /* ─── Derived ─── */

  const statusChartData = useMemo(() => {
    if (!complaints?.byStatus) return [];
    return complaints.byStatus.map(s => ({
      name: s.name,
      value: s.value,
      fill: STATUS_COLORS[s.name] || PALETTE.subtle,
    }));
  }, [complaints]);

  const priorityChartData = useMemo(() => {
    if (!complaintsAnalytics?.byPriority) return [];
    return complaintsAnalytics.byPriority.map((p, idx) => ({
      name: p.name,
      value: p.total,
      fill: PRIORITY_COLORS[idx] || PALETTE.subtle,
    }));
  }, [complaintsAnalytics]);

  const activityChartData = useMemo(() => {
    if (!workers?.activity_profile) return [];
    return workers.activity_profile.map((a, idx) => ({
      name: a.name,
      value: a.value,
      fill: ACTIVITY_COLORS[idx % ACTIVITY_COLORS.length],
    }));
  }, [workers]);

  const topCompanies = useMemo(() => {
    if (!complaints?.byCompanies) return [];
    return [...complaints.byCompanies]
      .sort((a, b) => b.complaints - a.complaints)
      .slice(0, 6);
  }, [complaints]);
  const topCompanyMax = topCompanies[0]?.complaints || 1;

  const complaintsMonthly = useMemo(() => {
    if (!complaintsAnalytics?.byMonth) return [];
    return complaintsAnalytics.byMonth.map(m => ({
      month: formatMonthLabel(m.month),
      total: m.total,
    }));
  }, [complaintsAnalytics]);

  const workersMonthly = useMemo(() => {
    if (!workersAnalytics?.byMonth) return [];
    return workersAnalytics.byMonth.map(m => ({
      month: formatMonthLabel(m.month),
      total: m.total,
    }));
  }, [workersAnalytics]);

  const trendData =
    trendMode === 'complaints' ? complaintsMonthly : workersMonthly;
  const trendAnalytics =
    trendMode === 'complaints' ? complaintsAnalytics : workersAnalytics;
  const trendColor =
    trendMode === 'complaints' ? PALETTE.orange : PALETTE.green;

  const trendLatest = trendData[trendData.length - 1]?.total || 0;
  const trendPrevious = trendData[trendData.length - 2]?.total || 0;
  const trendDelta =
    trendPrevious > 0
      ? Math.round(((trendLatest - trendPrevious) / trendPrevious) * 100)
      : 0;

  const mapData =
    mapMode === 'complaints'
      ? complaintsAnalytics?.byCity || []
      : workersAnalytics?.byCity || [];

  const unionizationRate = workersAnalytics?.unionization?.rate ?? 0;
  const gaugeData = [
    { name: 'unionized', value: unionizationRate },
    { name: 'rest', value: Math.max(0, 100 - unionizationRate) },
  ];

  const statusTotal = useMemo(
    () => statusChartData.reduce((acc, s) => acc + s.value, 0),
    [statusChartData],
  );

  const priorityTotal = useMemo(
    () => priorityChartData.reduce((acc, p) => acc + p.value, 0),
    [priorityChartData],
  );

  const activityTotal = useMemo(
    () => activityChartData.reduce((acc, a) => acc + a.value, 0),
    [activityChartData],
  );

  /* ─── Render ─── */

  return (
    <BasePage title="Dashboard">
      <Container
        fluid
        style={{
          background: '#fafbfc',
          padding: '20px',
          borderRadius: 16,
        }}
      >
        <DashboardContainer>
          <TopBar>
            <div>
              <h2>
                {isAdmin
                  ? 'Visão Geral'
                  : `Painel · ${user.syndicate?.nome_fantasia || 'Sindicato'}`}
              </h2>
              <div className="subtitle">
                {isAdmin
                  ? 'Indicadores consolidados de todas as entidades'
                  : 'Indicadores do seu sindicato'}
              </div>
            </div>
            <div className="updated">
              <FiClock size={12} />
              {`Atualizado em ${format(updatedAt, "dd/MM/yyyy 'às' HH:mm")}`}
            </div>
          </TopBar>

          {/* ───── KPI row ───── */}
          <KpiRow>
            <KpiCell
              $tone="orange"
              $clickable={!!overview}
              onClick={() => overview && history.push('/complaints')}
            >
              {overview ? (
                <>
                  <div className="label">
                    <span className="icon">
                      <FiAlertCircle size={14} />
                    </span>
                    Denúncias totais
                  </div>
                  <div className="value">
                    {formatNumber(overview.totalComplaints || 0)}
                  </div>
                  <div className="delta">
                    <span className="chip warn">
                      {`${formatNumber(
                        overview.openComplaints || 0,
                      )} em aberto`}
                    </span>
                  </div>
                </>
              ) : (
                <KpiSkeleton />
              )}
            </KpiCell>

            <KpiCell
              $tone="green"
              $clickable={!!overview}
              onClick={() => overview && history.push('/workers')}
            >
              {overview ? (
                <>
                  <div className="label">
                    <span className="icon">
                      <FiUsers size={14} />
                    </span>
                    Trabalhadores
                  </div>
                  <div className="value">
                    {formatNumber(overview.totalWorkers || 0)}
                  </div>
                  <div className="delta">
                    {workersAnalytics ? (
                      <span className="chip">
                        {`${unionizationRate.toFixed(1)}% sindicalizados`}
                      </span>
                    ) : (
                      <Skeleton $w={120} $h={18} $radius={999} />
                    )}
                  </div>
                </>
              ) : (
                <KpiSkeleton />
              )}
            </KpiCell>

            <KpiCell $tone="blue">
              {overview ? (
                <>
                  <div className="label">
                    <span className="icon">
                      <FiBriefcase size={14} />
                    </span>
                    Empresas
                  </div>
                  <div className="value">
                    {formatNumber(overview.totalCompanies || 0)}
                  </div>
                  <div className="delta">
                    <FiMapPin size={12} />
                    {complaintsAnalytics ? (
                      `${formatNumber(
                        complaintsAnalytics.byCity?.length || 0,
                      )} cidades com registros`
                    ) : (
                      <Skeleton $w={160} $h={12} />
                    )}
                  </div>
                </>
              ) : (
                <KpiSkeleton />
              )}
            </KpiCell>

            {isAdmin ? (
              <KpiCell $tone="neutral">
                {overview ? (
                  <>
                    <div className="label">
                      <span className="icon">
                        <FiHome size={14} />
                      </span>
                      Sindicatos
                    </div>
                    <div className="value">
                      {formatNumber(overview.totalSyndicates || 0)}
                    </div>
                    <div className="delta">
                      <FiFileText size={12} />
                      {`${formatNumber(
                        overview.totalPosts || 0,
                      )} notícias publicadas`}
                    </div>
                  </>
                ) : (
                  <KpiSkeleton />
                )}
              </KpiCell>
            ) : (
              <KpiCell $tone="neutral">
                {workersAnalytics ? (
                  <>
                    <div className="label">
                      <span className="icon">
                        <FiUserPlus size={14} />
                      </span>
                      Pré-cadastros
                    </div>
                    <div className="value">
                      {formatNumber(workersAnalytics.preRegistrations || 0)}
                    </div>
                    <div className="delta">
                      <FiClock size={12} />
                      aguardando conclusão
                    </div>
                  </>
                ) : (
                  <KpiSkeleton />
                )}
              </KpiCell>
            )}
          </KpiRow>

          {/* ───── Chart + map ───── */}
          <MiddleRow>
            {/* Evolução temporal */}
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Evolução mensal</h3>
                  <span className="subtitle">
                    {trendMode === 'complaints'
                      ? 'Volume de denúncias por mês'
                      : 'Novos trabalhadores cadastrados por mês'}
                  </span>
                </div>
                <div className="actions">
                  {trendAnalytics && (
                    <InsightPill
                      className={trendDelta >= 0 ? 'orange' : 'blue'}
                    >
                      <FiTrendingUp size={12} />
                      {`${
                        trendDelta >= 0 ? '+' : ''
                      }${trendDelta}% vs. anterior`}
                    </InsightPill>
                  )}
                  <ToggleGroup>
                    <ToggleButton
                      type="button"
                      $active={trendMode === 'complaints'}
                      onClick={() => setTrendMode('complaints')}
                    >
                      <FiAlertCircle size={12} />
                      Denúncias
                    </ToggleButton>
                    <ToggleButton
                      type="button"
                      $active={trendMode === 'workers'}
                      onClick={() => setTrendMode('workers')}
                    >
                      <FiUsers size={12} />
                      Trabalhadores
                    </ToggleButton>
                  </ToggleGroup>
                </div>
              </CardHeader>

              <ChartBody>
                {!trendAnalytics && <ChartSkeleton height={240} />}
                {trendAnalytics && trendData.length === 0 && (
                  <EmptyState>Sem dados para exibir</EmptyState>
                )}
                {trendAnalytics && trendData.length > 0 && (
                  <FadeIn style={{ height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={trendData}
                        margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                        barCategoryGap="8%"
                      >
                        <CartesianGrid
                          vertical={false}
                          stroke={PALETTE.neutral100}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: PALETTE.textMuted, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: PALETTE.textMuted, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          width={30}
                        />
                        <Tooltip
                          content={<ChartTooltip valueLabel="Total" />}
                          cursor={{ fill: PALETTE.neutral50 }}
                        />
                        <Bar
                          dataKey="total"
                          fill={trendColor}
                          radius={[6, 6, 0, 0]}
                          maxBarSize={72}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </FadeIn>
                )}
              </ChartBody>
            </Card>

            {/* Mapa */}
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Distribuição geográfica</h3>
                  <span className="subtitle">
                    Concentração por estado do Brasil
                  </span>
                </div>
                <ToggleGroup>
                  <ToggleButton
                    type="button"
                    $active={mapMode === 'complaints'}
                    onClick={() => setMapMode('complaints')}
                  >
                    <FiAlertCircle size={12} />
                    Denúncias
                  </ToggleButton>
                  <ToggleButton
                    type="button"
                    $active={mapMode === 'workers'}
                    onClick={() => setMapMode('workers')}
                  >
                    <FiUsers size={12} />
                    Trabalhadores
                  </ToggleButton>
                </ToggleGroup>
              </CardHeader>

              <BrazilMap data={mapData} mode={mapMode} />
              <MapLegend>
                <span className="item">
                  <span
                    className="dot"
                    style={{
                      background:
                        mapMode === 'complaints'
                          ? PALETTE.orange
                          : PALETTE.green,
                    }}
                  />
                  {`${
                    mapMode === 'complaints' ? 'Denúncias' : 'Trabalhadores'
                  } por estado`}
                </span>
                <span className="item">
                  {mapData.length > 0
                    ? 'tamanho do círculo = volume'
                    : 'sem registros neste recorte'}
                </span>
              </MapLegend>
            </Card>
          </MiddleRow>

          {/* ───── Triple row: status + priority + unionization ───── */}
          <TripleRow>
            {/* Status */}
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Denúncias por status</h3>
                  <span className="subtitle">Funil de atendimento</span>
                </div>
              </CardHeader>
              {!complaints && <DonutSkeleton />}
              {complaints &&
                !(statusChartData.length > 0 && statusTotal > 0) && (
                  <EmptyState>Sem dados</EmptyState>
                )}
              {complaints && statusChartData.length > 0 && statusTotal > 0 && (
                <FadeIn>
                  <DonutLayout>
                    <DonutCenter>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={statusChartData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={50}
                            outerRadius={75}
                            paddingAngle={2}
                            stroke="none"
                          >
                            {statusChartData.map(e => (
                              <Cell key={e.name} fill={e.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<ChartTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="center">
                        <span className="big">{formatNumber(statusTotal)}</span>
                        <span className="small">Total</span>
                      </div>
                    </DonutCenter>
                    <LegendList>
                      {statusChartData.map(e => (
                        <LegendItem key={e.name}>
                          <span
                            className="dot"
                            style={{ background: e.fill }}
                          />
                          <span className="name">{e.name}</span>
                          <span className="value">{formatNumber(e.value)}</span>
                          <span className="percent">
                            {`${percent(e.value, statusTotal)}%`}
                          </span>
                        </LegendItem>
                      ))}
                    </LegendList>
                  </DonutLayout>
                </FadeIn>
              )}
            </Card>

            {/* Priority */}
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Prioridade das denúncias</h3>
                  <span className="subtitle">
                    {complaintsAnalytics?.avgResolutionDays != null
                      ? `Resolução média em ${complaintsAnalytics.avgResolutionDays.toFixed(
                          1,
                        )} dias`
                      : 'Distribuição por nível'}
                  </span>
                </div>
              </CardHeader>
              {!complaintsAnalytics && <DonutSkeleton />}
              {complaintsAnalytics &&
                !(priorityChartData.length > 0 && priorityTotal > 0) && (
                  <EmptyState>Sem dados</EmptyState>
                )}
              {complaintsAnalytics &&
                priorityChartData.length > 0 &&
                priorityTotal > 0 && (
                  <FadeIn>
                    <DonutLayout>
                      <DonutCenter>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={priorityChartData}
                              dataKey="value"
                              nameKey="name"
                              innerRadius={50}
                              outerRadius={75}
                              paddingAngle={2}
                              stroke="none"
                            >
                              {priorityChartData.map(e => (
                                <Cell key={e.name} fill={e.fill} />
                              ))}
                            </Pie>
                            <Tooltip content={<ChartTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="center">
                          <span className="big">
                            {complaintsAnalytics?.avgResolutionDays != null
                              ? `${complaintsAnalytics.avgResolutionDays.toFixed(
                                  1,
                                )}d`
                              : formatNumber(priorityTotal)}
                          </span>
                          <span className="small">
                            {complaintsAnalytics?.avgResolutionDays != null
                              ? 'Tempo médio'
                              : 'Total'}
                          </span>
                        </div>
                      </DonutCenter>
                      <LegendList>
                        {priorityChartData.map(e => (
                          <LegendItem key={e.name}>
                            <span
                              className="dot"
                              style={{ background: e.fill }}
                            />
                            <span className="name">{e.name}</span>
                            <span className="value">
                              {formatNumber(e.value)}
                            </span>
                            <span className="percent">
                              {`${percent(e.value, priorityTotal)}%`}
                            </span>
                          </LegendItem>
                        ))}
                      </LegendList>
                    </DonutLayout>
                  </FadeIn>
                )}
            </Card>

            {/* Unionization gauge */}
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Sindicalização</h3>
                  <span className="subtitle">
                    Taxa de trabalhadores filiados
                  </span>
                </div>
                {workersAnalytics && (
                  <InsightPill>
                    <FiUserCheck size={12} />
                    {`${formatNumber(
                      workersAnalytics.unionization?.unionized || 0,
                    )} filiados`}
                  </InsightPill>
                )}
              </CardHeader>

              {!workersAnalytics ? (
                <GaugeSkeleton />
              ) : (
                <FadeIn>
                  <GaugeCenter>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={gaugeData}
                          dataKey="value"
                          startAngle={180}
                          endAngle={0}
                          cx="50%"
                          cy="85%"
                          innerRadius={60}
                          outerRadius={85}
                          stroke="none"
                        >
                          <Cell fill={PALETTE.green} />
                          <Cell fill={PALETTE.neutral100} />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <span className="big">
                      {`${unionizationRate.toFixed(1)}%`}
                    </span>
                    <span className="small">Taxa de sindicalização</span>

                    <div className="breakdown">
                      <div className="item">
                        <strong>
                          {formatNumber(
                            workersAnalytics.unionization?.unionized || 0,
                          )}
                        </strong>
                        <span>Sindicalizados</span>
                      </div>
                      <div className="item">
                        <strong>
                          {formatNumber(
                            workersAnalytics.unionization?.notUnionized || 0,
                          )}
                        </strong>
                        <span>Não filiados</span>
                      </div>
                    </div>
                  </GaugeCenter>
                </FadeIn>
              )}
            </Card>
          </TripleRow>

          {/* ───── Bottom row: top companies + activity + research ───── */}
          <TripleRow>
            {/* Top companies */}
            <Card style={{ gridColumn: 'span 2' }}>
              <CardHeader>
                <div className="titles">
                  <h3>Top empresas com mais denúncias</h3>
                  <span className="subtitle">
                    Ranking por volume registrado
                  </span>
                </div>
                {complaints && (
                  <InsightPill className="orange">
                    <FiTarget size={12} />
                    {`${formatNumber(topCompanies.length)} empresas`}
                  </InsightPill>
                )}
              </CardHeader>
              {!complaints && <RankedBarsSkeleton />}
              {complaints && topCompanies.length === 0 && (
                <EmptyState>Nenhuma denúncia registrada</EmptyState>
              )}
              {complaints && topCompanies.length > 0 && (
                <FadeIn>
                  <RankedBarList>
                    {topCompanies.map((company, index) => {
                      const width = (company.complaints / topCompanyMax) * 100;
                      const pct = percent(
                        company.complaints,
                        complaints?.complaints || 0,
                      );
                      return (
                        <RankedBarRow key={company.name}>
                          <span className="rank">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="row-body">
                            <span className="row-name" title={company.name}>
                              {company.name}
                            </span>
                            <div className="row-track">
                              <div
                                className={`row-fill${
                                  index === 0 ? ' green' : ''
                                }`}
                                style={{ width: `${width}%` }}
                              />
                            </div>
                          </div>
                          <span className="row-value">
                            {formatNumber(company.complaints)}
                            <span
                              style={{
                                color: PALETTE.subtle,
                                fontWeight: 500,
                                marginLeft: 6,
                                fontSize: 11,
                              }}
                            >
                              {`${pct}%`}
                            </span>
                          </span>
                        </RankedBarRow>
                      );
                    })}
                  </RankedBarList>
                </FadeIn>
              )}
            </Card>

            {/* Research participation */}
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Pesquisas</h3>
                  <span className="subtitle">
                    Participação dos trabalhadores
                  </span>
                </div>
              </CardHeader>
              {!researchAnalytics ? (
                <ResearchSkeleton />
              ) : (
                <FadeIn>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 12,
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        background: PALETTE.greenSoft,
                        border: `1px solid ${PALETTE.greenBorder}`,
                        borderRadius: 10,
                        padding: 12,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: PALETTE.green,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {formatNumber(researchAnalytics?.active || 0)}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: PALETTE.textMuted,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          marginTop: 2,
                        }}
                      >
                        Ativas
                      </div>
                    </div>
                    <div
                      style={{
                        background: PALETTE.neutral50,
                        border: `1px solid ${PALETTE.neutral200}`,
                        borderRadius: 10,
                        padding: 12,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: PALETTE.text,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {formatNumber(researchAnalytics?.total || 0)}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: PALETTE.textMuted,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          marginTop: 2,
                        }}
                      >
                        Total
                      </div>
                    </div>
                    <div
                      style={{
                        background: PALETTE.neutral50,
                        border: `1px solid ${PALETTE.neutral200}`,
                        borderRadius: 10,
                        padding: 12,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: PALETTE.text,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {formatNumber(researchAnalytics?.finished || 0)}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: PALETTE.textMuted,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          marginTop: 2,
                        }}
                      >
                        Finalizadas
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      color: PALETTE.textMuted,
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    <span>Taxa de participação</span>
                    <strong
                      style={{
                        color: PALETTE.text,
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      {`${(researchAnalytics?.participation?.rate || 0).toFixed(
                        1,
                      )}%`}
                    </strong>
                  </div>
                  <div
                    style={{
                      background: PALETTE.neutral100,
                      borderRadius: 999,
                      height: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(
                          100,
                          researchAnalytics?.participation?.rate || 0,
                        )}%`,
                        height: '100%',
                        background: PALETTE.green,
                        borderRadius: 999,
                        transition: 'width 0.8s ease',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: PALETTE.textMuted,
                      marginTop: 8,
                      fontWeight: 500,
                    }}
                  >
                    {`${formatNumber(
                      researchAnalytics?.participation?.participants || 0,
                    )} de ${formatNumber(
                      researchAnalytics?.participation?.totalWorkers || 0,
                    )} trabalhadores`}
                  </div>
                </FadeIn>
              )}
            </Card>
          </TripleRow>

          {/* ───── Bottom row 2: activity profile + categories ───── */}
          <TripleRow>
            <Card>
              <CardHeader>
                <div className="titles">
                  <h3>Perfil de atividade</h3>
                  <span className="subtitle">Trabalhadores por categoria</span>
                </div>
              </CardHeader>
              {!workers && <DonutSkeleton />}
              {workers &&
                !(activityChartData.length > 0 && activityTotal > 0) && (
                  <EmptyState>Sem dados</EmptyState>
                )}
              {workers && activityChartData.length > 0 && activityTotal > 0 && (
                <FadeIn>
                  <DonutLayout>
                    <DonutCenter>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={activityChartData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={50}
                            outerRadius={75}
                            paddingAngle={2}
                            stroke="none"
                          >
                            {activityChartData.map(e => (
                              <Cell key={e.name} fill={e.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<ChartTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="center">
                        <span className="big">
                          {formatNumber(activityTotal)}
                        </span>
                        <span className="small">Trabalhadores</span>
                      </div>
                    </DonutCenter>
                    <LegendList>
                      {activityChartData.map(e => (
                        <LegendItem key={e.name}>
                          <span
                            className="dot"
                            style={{ background: e.fill }}
                          />
                          <span className="name">{e.name}</span>
                          <span className="value">{formatNumber(e.value)}</span>
                          <span className="percent">
                            {`${percent(e.value, activityTotal)}%`}
                          </span>
                        </LegendItem>
                      ))}
                    </LegendList>
                  </DonutLayout>
                </FadeIn>
              )}
            </Card>

            <Card style={{ gridColumn: 'span 2' }}>
              <CardHeader>
                <div className="titles">
                  <h3>Composição dos trabalhadores</h3>
                  <span className="subtitle">
                    Distribuição por gênero, categoria e vínculo
                  </span>
                </div>
                {workers && (
                  <InsightPill>
                    <FiUsers size={12} />
                    {`${formatNumber(workers.workers || 0)} no total`}
                  </InsightPill>
                )}
              </CardHeader>

              {!workers && <CompositionSkeleton />}

              {workers && (
                <FadeIn>
                  {/* Gender — stacked bar */}
                  {workers?.gender && workers.gender.length > 0 && (
                    <CompositionSection>
                      <div className="section-label">Gênero</div>
                      <StackedBar>
                        {workers.gender.map((g, idx) => {
                          const total = workers?.workers || 1;
                          const pct = (g.value / total) * 100;
                          const color = getGenderColor(idx);
                          return (
                            <div
                              key={g.name}
                              className="segment"
                              style={{ width: `${pct}%`, background: color }}
                              title={`${g.name}: ${formatNumber(
                                g.value,
                              )} (${pct.toFixed(1)}%)`}
                            >
                              {pct >= 12 && (
                                <span className="segment-label">
                                  {`${pct.toFixed(0)}%`}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </StackedBar>
                      <div className="legend-row">
                        {workers.gender.map((g, idx) => {
                          const total = workers?.workers || 1;
                          const pct = percent(g.value, total);
                          const color = getGenderColor(idx);
                          return (
                            <div key={g.name} className="legend">
                              <span
                                className="dot"
                                style={{ background: color }}
                              />
                              <span className="name">{g.name}</span>
                              <span className="value">
                                {formatNumber(g.value)}
                              </span>
                              <span className="percent">{`${pct}%`}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CompositionSection>
                  )}

                  {/* Categories + vínculo — stat tiles */}
                  <CompositionSection>
                    <div className="section-label">Categorias e vínculo</div>
                    <StatTiles>
                      {(workers?.category || []).map((c, idx) => {
                        const total = workers?.workers || 1;
                        const pct = percent(c.value, total);
                        const tone = idx === 0 ? 'orange' : 'green';
                        return (
                          <StatTile key={c.name} $tone={tone}>
                            <div className="icon">
                              {tone === 'orange' ? (
                                <FiBriefcase size={16} />
                              ) : (
                                <FiActivity size={16} />
                              )}
                            </div>
                            <div className="body">
                              <div className="value">
                                {formatNumber(c.value)}
                                <span className="pct">{`${pct}%`}</span>
                              </div>
                              <div className="label">{c.name}</div>
                            </div>
                          </StatTile>
                        );
                      })}

                      <StatTile $tone="blue">
                        <div className="icon">
                          <FiUserPlus size={16} />
                        </div>
                        <div className="body">
                          <div className="value">
                            {formatNumber(
                              workersAnalytics?.preRegistrations || 0,
                            )}
                          </div>
                          <div className="label">Pré-cadastros pendentes</div>
                        </div>
                      </StatTile>

                      <StatTile $tone="greenSoft">
                        <div className="icon">
                          <FiUserCheck size={16} />
                        </div>
                        <div className="body">
                          <div className="value">
                            {formatNumber(
                              workersAnalytics?.unionization?.unionized || 0,
                            )}
                            <span className="pct">
                              {`${unionizationRate.toFixed(0)}%`}
                            </span>
                          </div>
                          <div className="label">Sindicalizados</div>
                        </div>
                      </StatTile>
                    </StatTiles>
                  </CompositionSection>
                </FadeIn>
              )}
            </Card>
          </TripleRow>
        </DashboardContainer>
      </Container>
    </BasePage>
  );
};

export default Dashboard;
